import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FaWhatsapp } from "react-icons/fa";
import { FiSend, FiX } from "react-icons/fi";
import { startCrmWhatsAppConversation } from "../crmApi";
import { useCrmCopy } from "../crmI18n";

const emptyForm = {
  template_name: "",
  language_code: "es_ES",
  parameters: "",
};

function hasPhone(value) {
  return Boolean(String(value || "").trim());
}

export default function WhatsAppStartButton({
  phone,
  label,
  contactId = null,
  leadId = null,
  disabled = false,
  disabledTitle = "",
  className = "",
  onStarted,
}) {
  const copy = useCrmCopy();
  const activityCopy = copy.entities.activity;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const canStart = hasPhone(phone) && !disabled;
  const title = canStart ? `WhatsApp ${label || phone}` : disabledTitle || "Sin telefono";

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const close = () => {
    if (sending) return;
    setOpen(false);
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canStart || !form.template_name.trim()) return;
    setSending(true);
    setError("");

    try {
      const response = await startCrmWhatsAppConversation({
        contact_customer_id: contactId || undefined,
        lead_id: leadId || undefined,
        phone,
        template_name: form.template_name,
        language_code: form.language_code || "es_ES",
        parameters: form.parameters,
        subject: label ? `WhatsApp chat with ${label}` : "",
      });
      setForm(emptyForm);
      setOpen(false);
      onStarted?.(response?.conversation);
    } catch (err) {
      console.error("[CRM] WhatsApp start error:", err);
      setError(err?.response?.data?.error || activityCopy.whatsappTemplateSendError);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`crm-iconButton crm-whatsappStartButton ${className}`}
        onClick={() => canStart && setOpen(true)}
        disabled={!canStart}
        title={title}
        aria-label={title}
      >
        <FaWhatsapp aria-hidden="true" />
      </button>

      {open
        ? createPortal(
            <div className="crm-modalBackdrop" role="presentation">
              <form className="crm-modal crm-modal--sm" onSubmit={submit}>
                <div className="crm-modalHeader">
                  <div>
                    <h2>WhatsApp</h2>
                    <span>{label || phone}</span>
                  </div>
                  <button type="button" className="crm-iconButton" onClick={close} aria-label={copy.common.cancel}>
                    <FiX aria-hidden="true" />
                  </button>
                </div>

                {error ? <div className="crm-error">{error}</div> : null}

                <div className="crm-formGrid">
                  <label className="crm-field">
                    <span>{activityCopy.whatsappTemplateName}</span>
                    <input
                      value={form.template_name}
                      onChange={(event) => updateField("template_name", event.target.value)}
                      required
                    />
                  </label>
                  <label className="crm-field">
                    <span>{activityCopy.whatsappTemplateLanguage}</span>
                    <input
                      value={form.language_code}
                      onChange={(event) => updateField("language_code", event.target.value)}
                    />
                  </label>
                  <label className="crm-field crm-field--wide">
                    <span>{activityCopy.whatsappTemplateParameters}</span>
                    <textarea
                      value={form.parameters}
                      onChange={(event) => updateField("parameters", event.target.value)}
                      rows={4}
                    />
                  </label>
                </div>

                <div className="crm-modalActions">
                  <button type="button" className="crm-ghostButton" onClick={close} disabled={sending}>
                    {copy.common.cancel}
                  </button>
                  <button type="submit" className="crm-primaryButton" disabled={sending || !form.template_name.trim()}>
                    <FiSend aria-hidden="true" />
                    <span>{sending ? copy.common.saving : activityCopy.whatsappTemplateSend}</span>
                  </button>
                </div>
              </form>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
